import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import AdminLayout from "@/components/layout-admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DeleteConfirmDialog } from "@/components/ui/delete-confirm-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import {
  MessageSquare, Plus, Send, Search, Phone, Mail, User, Clock,
  Filter, ChevronLeft, Globe, MessageCircle, X, Tag,
  Star, AlertCircle, ArrowRight, FileText, UserCheck, Inbox,
  CheckCircle2, CircleDot, Archive, Kanban, List, StickyNote,
  CalendarDays, Users, Zap, GripVertical,
  CheckSquare, Circle, Sparkles, History, BookOpen, DollarSign,
  Building, Trash2, Edit3,
} from "lucide-react";
import type { CrmConversation, CrmMessage, CrmActivity, CrmTask, CrmTemplate } from "@shared/schema";

const channelIcons: Record<string, typeof MessageSquare> = {
  internal: MessageCircle, email: Mail, sms: Phone, whatsapp: MessageSquare,
};

const channelColors: Record<string, string> = {
  internal: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
  email: "bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300",
  sms: "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300",
  whatsapp: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
};

const statusColors: Record<string, string> = {
  open: "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300",
  closed: "bg-gray-100 text-gray-700 dark:bg-gray-900/50 dark:text-gray-300",
  pending: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300",
};

const pipelineStages = [
  { id: "new", label: "New", icon: Inbox, color: "border-blue-300 dark:border-blue-700", headerBg: "bg-blue-50 dark:bg-blue-950/40", dotColor: "bg-blue-500" },
  { id: "contacted", label: "Contacted", icon: MessageCircle, color: "border-purple-300 dark:border-purple-700", headerBg: "bg-purple-50 dark:bg-purple-950/40", dotColor: "bg-purple-500" },
  { id: "qualified", label: "Qualified", icon: UserCheck, color: "border-amber-300 dark:border-amber-700", headerBg: "bg-amber-50 dark:bg-amber-950/40", dotColor: "bg-amber-500" },
  { id: "proposal", label: "Proposal", icon: FileText, color: "border-orange-300 dark:border-orange-700", headerBg: "bg-orange-50 dark:bg-orange-950/40", dotColor: "bg-orange-500" },
  { id: "booked", label: "Booked", icon: CheckCircle2, color: "border-green-300 dark:border-green-700", headerBg: "bg-green-50 dark:bg-green-950/40", dotColor: "bg-green-500" },
  { id: "closed", label: "Closed", icon: Archive, color: "border-gray-300 dark:border-gray-700", headerBg: "bg-gray-50 dark:bg-gray-950/40", dotColor: "bg-gray-400" },
];

const priorityConfig: Record<string, { color: string; label: string; icon: typeof Star }> = {
  low: { color: "text-gray-400", label: "Low", icon: Circle },
  normal: { color: "text-blue-500", label: "Normal", icon: CircleDot },
  high: { color: "text-orange-500", label: "High", icon: Star },
  urgent: { color: "text-red-500", label: "Urgent", icon: AlertCircle },
};

const defaultTags = ["VIP", "Repeat Guest", "Long Stay", "Corporate", "Family", "Group", "Complaint", "Review Request", "Upsell"];
const templateCategories = ["general", "check-in", "check-out", "payment", "inquiry", "follow-up", "complaint"];

const activityTypeIcons: Record<string, typeof MessageSquare> = {
  status_change: CircleDot,
  pipeline_change: ArrowRight,
  assignment: Users,
  priority_change: Star,
  tag_added: Tag,
  tag_removed: X,
  task_created: CheckSquare,
  task_completed: CheckCircle2,
  bulk_status: Zap,
  bulk_pipelineStage: Zap,
  bulk_assignedTo: Zap,
  bulk_priority: Zap,
  message_sent: Send,
  message_received: Inbox,
};

function DraggableCard({ conv, onClick, onDragStart }: { conv: CrmConversation; onClick: () => void; onDragStart?: (e: React.DragEvent, conv: CrmConversation) => void }) {
  const ChannelIcon = channelIcons[conv.platform] || Globe;
  const prio = priorityConfig[conv.priority || 'normal'];
  const convTags = (conv.tags as string[]) || [];

  return (
    <button
      draggable
      onDragStart={(e) => onDragStart?.(e, conv)}
      onClick={onClick}
      className="w-full text-left bg-card border rounded-lg p-3 hover:shadow-md transition-all group cursor-grab active:cursor-grabbing"
      data-testid={`pipeline-card-${conv.id}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-sm font-medium truncate flex-1">{conv.guestName}</p>
        <div className="flex items-center gap-1">
          {(conv.priority === 'urgent' || conv.priority === 'high') && (
            <prio.icon className={`w-3.5 h-3.5 ${prio.color} shrink-0`} />
          )}
          <GripVertical className="w-3.5 h-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
      <div className="flex items-center gap-1.5 mb-1">
        <ChannelIcon className="w-3 h-3 text-muted-foreground" />
        <span className="text-[10px] text-muted-foreground capitalize">{conv.platform}</span>
        {conv.source && <span className="text-[10px] text-muted-foreground/70">via {conv.source}</span>}
      </div>
      <div className="flex items-center justify-between">
        <Badge variant="secondary" className={`text-[9px] px-1.5 py-0 ${statusColors[conv.status]}`}>{conv.status}</Badge>
        {conv.lastMessageAt && (
          <span className="text-[9px] text-muted-foreground">
            {new Date(conv.lastMessageAt).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })}
          </span>
        )}
      </div>
      {convTags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {convTags.slice(0, 2).map(tag => (
            <span key={tag} className="text-[8px] px-1.5 py-0 rounded-full bg-[#C2652A]/8 text-[#C2652A] border border-[#C2652A]/15">{tag}</span>
          ))}
          {convTags.length > 2 && <span className="text-[8px] text-muted-foreground">+{convTags.length - 2}</span>}
        </div>
      )}
    </button>
  );
}

export default function AdminCRM() {
  const { toast } = useToast();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [newConvOpen, setNewConvOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterChannel, setFilterChannel] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [filterAssigned, setFilterAssigned] = useState<string>("all");
  const [messageText, setMessageText] = useState("");
  const [viewMode, setViewMode] = useState<"list" | "pipeline">("list");
  const [detailTab, setDetailTab] = useState<"messages" | "activity" | "tasks">("messages");
  const [showContactPanel, setShowContactPanel] = useState(true);
  const [tagInput, setTagInput] = useState("");
  const [noteText, setNoteText] = useState("");
  const [sendChannel, setSendChannel] = useState<string>("internal");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkBar, setShowBulkBar] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [draggedConv, setDraggedConv] = useState<CrmConversation | null>(null);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDue, setNewTaskDue] = useState("");
  const [newTaskPriority, setNewTaskPriority] = useState("normal");
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplateBody, setNewTemplateBody] = useState("");
  const [newTemplateCategory, setNewTemplateCategory] = useState("general");
  const [editingTemplate, setEditingTemplate] = useState<CrmTemplate | null>(null);
  const [editingContact, setEditingContact] = useState<any>(null);
  const [deleteConfirmCrmTask, setDeleteConfirmCrmTask] = useState<CrmTask | null>(null);
  const [deleteConfirmCrmTemplate, setDeleteConfirmCrmTemplate] = useState<CrmTemplate | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [newConv, setNewConv] = useState({
    guestName: "", guestEmail: "", guestPhone: "", guestAddress: "", guestCompany: "",
    guestLanguage: "en", platform: "internal",
    pipelineStage: "new", priority: "normal", source: "", tags: [] as string[],
    reservationId: "", apartmentId: "",
  });

  const { data: conversations = [], isLoading } = useQuery<CrmConversation[]>({
    queryKey: ["/api/admin/crm/conversations"],
  });

  const { data: messages = [] } = useQuery<CrmMessage[]>({
    queryKey: ["/api/admin/crm/conversations", selectedId, "messages"],
    enabled: !!selectedId,
  });

  useEffect(() => {
    if (selectedId && messages.length > 0) {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/conversations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/stats"] });
    }
  }, [selectedId]);

  const { data: activities = [] } = useQuery<CrmActivity[]>({
    queryKey: ["/api/admin/crm/conversations", selectedId, "activities"],
    enabled: !!selectedId,
  });

  const { data: tasks = [] } = useQuery<CrmTask[]>({
    queryKey: ["/api/admin/crm/conversations", selectedId, "tasks"],
    enabled: !!selectedId,
  });

  const { data: templates = [] } = useQuery<CrmTemplate[]>({
    queryKey: ["/api/admin/crm/templates"],
  });

  const { data: enrichedData } = useQuery<any>({
    queryKey: ["/api/admin/crm/conversations", selectedId, "enriched"],
    enabled: !!selectedId,
  });

  const { data: stats } = useQuery<any>({
    queryKey: ["/api/admin/crm/stats"],
  });

  const { data: teamMembers = [] } = useQuery<any[]>({
    queryKey: ["/api/admin/users"],
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const createConversation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/admin/crm/conversations", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/conversations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/stats"] });
      setNewConvOpen(false);
      setNewConv({ guestName: "", guestEmail: "", guestPhone: "", guestAddress: "", guestCompany: "", guestLanguage: "en", platform: "internal", pipelineStage: "new", priority: "normal", source: "", tags: [], reservationId: "", apartmentId: "" });
      toast({ title: "Conversation created" });
    },
  });

  const sendMessage = useMutation({
    mutationFn: (data: any) => apiRequest("POST", `/api/admin/crm/conversations/${selectedId}/messages`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/conversations", selectedId, "messages"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/conversations"] });
      setMessageText("");
    },
  });

  const updateConversation = useMutation({
    mutationFn: ({ id, ...data }: { id: number; [key: string]: any }) =>
      apiRequest("PATCH", `/api/admin/crm/conversations/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/conversations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/conversations", selectedId, "activities"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/stats"] });
    },
  });

  const bulkAction = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/admin/crm/conversations/bulk", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/conversations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/stats"] });
      setSelectedIds(new Set());
      setShowBulkBar(false);
      toast({ title: "Bulk action applied" });
    },
  });

  const createTask = useMutation({
    mutationFn: (data: any) => apiRequest("POST", `/api/admin/crm/conversations/${selectedId}/tasks`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/conversations", selectedId, "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/conversations", selectedId, "activities"] });
      setNewTaskTitle("");
      setNewTaskDue("");
      setNewTaskPriority("normal");
      setTaskDialogOpen(false);
      toast({ title: "Task created" });
    },
  });

  const updateTask = useMutation({
    mutationFn: ({ id, ...data }: { id: number; [key: string]: any }) =>
      apiRequest("PATCH", `/api/admin/crm/tasks/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/conversations", selectedId, "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/conversations", selectedId, "activities"] });
    },
  });

  const deleteTask = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/admin/crm/tasks/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/conversations", selectedId, "tasks"] });
    },
  });

  const createTemplate = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/admin/crm/templates", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/templates"] });
      setNewTemplateName("");
      setNewTemplateBody("");
      setNewTemplateCategory("general");
      toast({ title: "Template saved" });
    },
  });

  const updateTemplate = useMutation({
    mutationFn: ({ id, ...data }: { id: number; [key: string]: any }) =>
      apiRequest("PUT", `/api/admin/crm/templates/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/templates"] });
      setEditingTemplate(null);
      toast({ title: "Template updated" });
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/admin/crm/templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/templates"] });
      toast({ title: "Template deleted" });
    },
  });

  const filteredConversations = useMemo(() => conversations
    .filter((c) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!c.guestName.toLowerCase().includes(q) &&
            !(c.guestEmail || '').toLowerCase().includes(q) &&
            !(c.guestPhone || '').toLowerCase().includes(q) &&
            !(c.source || '').toLowerCase().includes(q)) return false;
      }
      if (filterChannel !== "all" && c.platform !== filterChannel) return false;
      if (filterStatus !== "all" && c.status !== filterStatus) return false;
      if (filterPriority !== "all" && c.priority !== filterPriority) return false;
      if (filterAssigned === "unassigned" && c.assignedTo) return false;
      if (filterAssigned === "assigned" && !c.assignedTo) return false;
      return true;
    })
    .sort((a, b) => new Date(b.lastMessageAt || 0).getTime() - new Date(a.lastMessageAt || 0).getTime()),
    [conversations, searchQuery, filterChannel, filterStatus, filterPriority, filterAssigned]
  );

  const selectedConversation = conversations.find((c) => c.id === selectedId);

  const pipelineCounts = useMemo(() => pipelineStages.map(stage => ({
    ...stage,
    count: conversations.filter(c => (c.pipelineStage || 'new') === stage.id).length,
    conversations: conversations
      .filter(c => (c.pipelineStage || 'new') === stage.id)
      .sort((a, b) => new Date(b.lastMessageAt || 0).getTime() - new Date(a.lastMessageAt || 0).getTime()),
  })), [conversations]);

  const addTag = (tag: string) => {
    if (!selectedConversation || !tag.trim()) return;
    const currentTags = (selectedConversation.tags as string[]) || [];
    if (currentTags.includes(tag.trim())) return;
    updateConversation.mutate({ id: selectedConversation.id, tags: [...currentTags, tag.trim()] });
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    if (!selectedConversation) return;
    const currentTags = (selectedConversation.tags as string[]) || [];
    updateConversation.mutate({ id: selectedConversation.id, tags: currentTags.filter(t => t !== tag) });
  };

  const saveNotes = () => {
    if (!selectedConversation) return;
    updateConversation.mutate({ id: selectedConversation.id, notes: noteText });
    toast({ title: "Notes saved" });
  };

  useEffect(() => {
    if (selectedConversation) {
      setNoteText(selectedConversation.notes || "");
    }
  }, [selectedConversation?.id]);

  const toggleSelectConv = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      setShowBulkBar(next.size > 0);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === filteredConversations.length) {
      setSelectedIds(new Set());
      setShowBulkBar(false);
    } else {
      setSelectedIds(new Set(filteredConversations.map(c => c.id)));
      setShowBulkBar(true);
    }
  };

  const handleNativeDragStart = (e: React.DragEvent, conv: CrmConversation) => {
    e.dataTransfer.setData("text/plain", String(conv.id));
    e.dataTransfer.effectAllowed = "move";
    setDraggedConv(conv);
  };

  const handleNativeDrop = (e: React.DragEvent, targetStage: string) => {
    e.preventDefault();
    const convId = Number(e.dataTransfer.getData("text/plain"));
    const conv = conversations.find(c => c.id === convId);
    if (conv && (conv.pipelineStage || 'new') !== targetStage) {
      updateConversation.mutate({ id: convId, pipelineStage: targetStage });
      toast({ title: `Moved to ${pipelineStages.find(s => s.id === targetStage)?.label}` });
    }
    setDraggedConv(null);
  };

  const useTemplate = (template: CrmTemplate) => {
    let body = template.body;
    if (selectedConversation) {
      body = body.replace(/\{\{guestName\}\}/g, selectedConversation.guestName || '');
      body = body.replace(/\{\{guestEmail\}\}/g, selectedConversation.guestEmail || '');
      body = body.replace(/\{\{guestPhone\}\}/g, selectedConversation.guestPhone || '');
    }
    setMessageText(body);
  };

  const getAssigneeName = (userId: number | null) => {
    if (!userId) return null;
    const member = teamMembers.find((m: any) => m.id === userId);
    return member?.fullName || member?.username || `User #${userId}`;
  };

  return (
    <AdminLayout>
      <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="crm-page">
        {/* Stats Bar */}
        <div className="border-b bg-white dark:bg-gray-900">
          <div className="flex items-center justify-between p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-[#C2652A]/10 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-[#C2652A]" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold" data-testid="crm-title">Guest CRM</h1>
                <p className="text-xs text-muted-foreground hidden sm:block">Manage guest communications & pipeline</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden md:flex items-center gap-3 mr-3">
                <div className="text-center px-3">
                  <p className="text-lg font-bold text-green-600">{stats?.open || 0}</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Open</p>
                </div>
                <div className="w-px h-8 bg-border" />
                <div className="text-center px-3">
                  <p className="text-lg font-bold text-yellow-600">{stats?.pending || 0}</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Pending</p>
                </div>
                <div className="w-px h-8 bg-border" />
                <div className="text-center px-3">
                  <p className="text-lg font-bold text-red-500">{stats?.overdueTasks || 0}</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Overdue</p>
                </div>
                <div className="w-px h-8 bg-border" />
                <div className="text-center px-3">
                  <p className="text-lg font-bold">{stats?.total || 0}</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total</p>
                </div>
              </div>
              <div className="flex items-center border rounded-lg p-0.5">
                <button onClick={() => setViewMode("list")} className={`p-1.5 rounded transition-colors ${viewMode === "list" ? "bg-[#C2652A]/10 text-[#C2652A]" : "text-muted-foreground"}`} data-testid="view-list">
                  <List className="h-4 w-4" />
                </button>
                <button onClick={() => setViewMode("pipeline")} className={`p-1.5 rounded transition-colors ${viewMode === "pipeline" ? "bg-[#C2652A]/10 text-[#C2652A]" : "text-muted-foreground"}`} data-testid="view-pipeline">
                  <Kanban className="h-4 w-4" />
                </button>
              </div>
              <Dialog open={newConvOpen} onOpenChange={setNewConvOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#C2652A] hover:bg-[#A8531E]" data-testid="new-conversation-btn">
                    <Plus className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">New Contact</span>
                    <span className="sm:hidden">New</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Plus className="h-5 w-5 text-[#C2652A]" />
                      New Contact
                    </DialogTitle>
                    <DialogDescription className="sr-only">Create a new CRM contact</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="text-xs font-medium mb-1 block text-muted-foreground">Guest Name *</label>
                        <Input placeholder="Full name" value={newConv.guestName} onChange={(e) => setNewConv({ ...newConv, guestName: e.target.value })} data-testid="input-guest-name" />
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1 block text-muted-foreground">Email</label>
                        <Input placeholder="guest@email.com" value={newConv.guestEmail} onChange={(e) => setNewConv({ ...newConv, guestEmail: e.target.value })} data-testid="input-guest-email" />
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1 block text-muted-foreground">Phone</label>
                        <Input placeholder="+1 234 567 8900" value={newConv.guestPhone} onChange={(e) => setNewConv({ ...newConv, guestPhone: e.target.value })} data-testid="input-guest-phone" />
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1 block text-muted-foreground">Channel</label>
                        <Select value={newConv.platform} onValueChange={(v) => setNewConv({ ...newConv, platform: v })}>
                          <SelectTrigger data-testid="select-channel"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="internal">Platform Chat</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="sms">SMS</SelectItem>
                            <SelectItem value="whatsapp">WhatsApp</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1 block text-muted-foreground">Priority</label>
                        <Select value={newConv.priority} onValueChange={(v) => setNewConv({ ...newConv, priority: v })}>
                          <SelectTrigger data-testid="select-priority"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="normal">Normal</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1 block text-muted-foreground">Pipeline Stage</label>
                        <Select value={newConv.pipelineStage} onValueChange={(v) => setNewConv({ ...newConv, pipelineStage: v })}>
                          <SelectTrigger data-testid="select-pipeline"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {pipelineStages.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1 block text-muted-foreground">Source</label>
                        <Input placeholder="e.g., Airbnb, Website" value={newConv.source || ""} onChange={(e) => setNewConv({ ...newConv, source: e.target.value })} data-testid="input-source" />
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1 block text-muted-foreground">Company</label>
                        <Input placeholder="Company name" value={newConv.guestCompany} onChange={(e) => setNewConv({ ...newConv, guestCompany: e.target.value })} data-testid="input-company" />
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1 block text-muted-foreground">Language</label>
                        <Select value={newConv.guestLanguage} onValueChange={(v) => setNewConv({ ...newConv, guestLanguage: v })}>
                          <SelectTrigger data-testid="select-language"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="en">English</SelectItem>
                            <SelectItem value="es">Spanish</SelectItem>
                            <SelectItem value="fr">French</SelectItem>
                            <SelectItem value="pt">Portuguese</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs font-medium mb-1 block text-muted-foreground">Address</label>
                        <Input placeholder="Guest address" value={newConv.guestAddress} onChange={(e) => setNewConv({ ...newConv, guestAddress: e.target.value })} data-testid="input-address" />
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1 block text-muted-foreground">Link Reservation</label>
                        <Input type="number" placeholder="ID" value={newConv.reservationId} onChange={(e) => setNewConv({ ...newConv, reservationId: e.target.value })} data-testid="input-reservation-id" />
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1 block text-muted-foreground">Link Apartment</label>
                        <Input type="number" placeholder="ID" value={newConv.apartmentId} onChange={(e) => setNewConv({ ...newConv, apartmentId: e.target.value })} data-testid="input-apartment-id" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1.5 block text-muted-foreground">Tags</label>
                      <div className="flex flex-wrap gap-1.5">
                        {defaultTags.map(tag => (
                          <button key={tag} type="button" onClick={() => {
                            if (newConv.tags.includes(tag)) setNewConv({ ...newConv, tags: newConv.tags.filter(t => t !== tag) });
                            else setNewConv({ ...newConv, tags: [...newConv.tags, tag] });
                          }} className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-full border transition-colors ${
                            newConv.tags.includes(tag) ? "bg-[#C2652A]/10 border-[#C2652A]/30 text-[#C2652A]" : "bg-muted border-transparent text-muted-foreground"
                          }`}>
                            {tag}
                          </button>
                        ))}
                      </div>
                    </div>
                    <Button className="w-full bg-[#C2652A] hover:bg-[#A8531E]" onClick={() => {
                      const { reservationId, apartmentId, ...rest } = newConv;
                      createConversation.mutate({
                        ...rest,
                        reservationId: reservationId ? Number(reservationId) : null,
                        apartmentId: apartmentId ? Number(apartmentId) : null,
                      });
                    }} disabled={!newConv.guestName || createConversation.isPending} data-testid="create-conversation-btn">
                      Create Contact
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Mobile Stats */}
          <div className="flex md:hidden items-center gap-2 px-3 pb-3 overflow-x-auto">
            <Badge variant="outline" className="whitespace-nowrap text-xs"><CircleDot className="w-3 h-3 mr-1 text-green-500" />{stats?.open || 0} open</Badge>
            <Badge variant="outline" className="whitespace-nowrap text-xs"><Clock className="w-3 h-3 mr-1 text-yellow-500" />{stats?.pending || 0} pending</Badge>
            <Badge variant="outline" className="whitespace-nowrap text-xs"><AlertCircle className="w-3 h-3 mr-1 text-red-500" />{stats?.overdueTasks || 0} overdue</Badge>
            <Badge variant="outline" className="whitespace-nowrap text-xs">{stats?.total || 0} total</Badge>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {showBulkBar && selectedIds.size > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-[#C2652A]/5 border-b flex-wrap" data-testid="bulk-action-bar">
            <Badge variant="secondary" className="text-xs">{selectedIds.size} selected</Badge>
            <Select onValueChange={(v) => bulkAction.mutate({ ids: Array.from(selectedIds), action: 'status', value: v })}>
              <SelectTrigger className="h-7 w-28 text-xs"><SelectValue placeholder="Status..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select onValueChange={(v) => bulkAction.mutate({ ids: Array.from(selectedIds), action: 'pipelineStage', value: v })}>
              <SelectTrigger className="h-7 w-28 text-xs"><SelectValue placeholder="Stage..." /></SelectTrigger>
              <SelectContent>
                {pipelineStages.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select onValueChange={(v) => bulkAction.mutate({ ids: Array.from(selectedIds), action: 'priority', value: v })}>
              <SelectTrigger className="h-7 w-28 text-xs"><SelectValue placeholder="Priority..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setSelectedIds(new Set()); setShowBulkBar(false); }}>
              <X className="h-3 w-3 mr-1" /> Clear
            </Button>
          </div>
        )}

        {/* Pipeline View */}
        {viewMode === "pipeline" ? (
          <div className="flex-1 overflow-x-auto p-4">
            <div className="flex gap-3 min-w-max h-full">
              {pipelineCounts.map(stage => {
                const StageIcon = stage.icon;
                return (
                  <div key={stage.id} className={`w-72 flex flex-col rounded-xl border-2 ${stage.color} bg-card`} data-testid={`pipeline-${stage.id}`}>
                    <div className={`p-3 rounded-t-[10px] flex items-center justify-between ${stage.headerBg}`}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${stage.dotColor}`} />
                        <span className="text-sm font-semibold">{stage.label}</span>
                      </div>
                      <Badge variant="secondary" className="text-xs font-bold">{stage.count}</Badge>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[100px]"
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-muted/50'); }}
                      onDragLeave={(e) => { e.currentTarget.classList.remove('bg-muted/50'); }}
                      onDrop={(e) => { e.currentTarget.classList.remove('bg-muted/50'); handleNativeDrop(e, stage.id); }}
                    >
                      {stage.conversations.map(conv => (
                        <DraggableCard
                          key={conv.id}
                          conv={conv}
                          onDragStart={handleNativeDragStart}
                          onClick={() => { setSelectedId(conv.id); setSendChannel(conv.platform || "internal"); setViewMode("list"); }}
                        />
                      ))}
                      {stage.conversations.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                          <StageIcon className="w-6 h-6 mb-2 opacity-30" />
                          <p className="text-xs opacity-50">No contacts</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* List View */
          <div className="flex flex-1 overflow-hidden">
            {/* Conversation List */}
            <div className={`${selectedId ? "hidden md:flex" : "flex"} flex-col w-full md:w-80 lg:w-96 border-r bg-gray-50/50 dark:bg-gray-950`}>
              <div className="p-3 space-y-2 border-b">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input placeholder="Search contacts..." className="pl-9" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-conversations" />
                  </div>
                  <Button variant={showFilters ? "secondary" : "outline"} size="icon" className="shrink-0" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters">
                    <Filter className="h-4 w-4" />
                  </Button>
                </div>
                {showFilters && (
                  <div className="grid grid-cols-2 gap-2" data-testid="filter-panel">
                    <Select value={filterChannel} onValueChange={setFilterChannel}>
                      <SelectTrigger className="h-8 text-xs" data-testid="filter-channel"><SelectValue placeholder="Channel" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Channels</SelectItem>
                        <SelectItem value="internal">Platform</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="sms">SMS</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="h-8 text-xs" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={filterPriority} onValueChange={setFilterPriority}>
                      <SelectTrigger className="h-8 text-xs" data-testid="filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Priority</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={filterAssigned} onValueChange={setFilterAssigned}>
                      <SelectTrigger className="h-8 text-xs" data-testid="filter-assigned"><SelectValue placeholder="Assignment" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="assigned">Assigned</SelectItem>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {filteredConversations.length > 0 && (
                  <div className="flex items-center justify-between px-1">
                    <div onClick={selectAll} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer" data-testid="select-all">
                      <Checkbox checked={selectedIds.size === filteredConversations.length && filteredConversations.length > 0} />
                      <span>Select all ({filteredConversations.length})</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto">
                {isLoading ? (
                  <div className="p-8 text-center">
                    <div className="animate-pulse space-y-3">
                      {[1,2,3].map(i => <div key={i} className="h-16 bg-muted rounded-lg" />)}
                    </div>
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="p-8 text-center">
                    <MessageSquare className="h-12 w-12 text-gray-200 dark:text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No contacts found</p>
                    <p className="text-gray-400 text-sm mt-1">Create a new contact to get started</p>
                  </div>
                ) : (
                  filteredConversations.map((conv) => {
                    const ChannelIcon = channelIcons[conv.platform] || Globe;
                    const convTags = (conv.tags as string[]) || [];
                    const prio = priorityConfig[conv.priority || 'normal'];
                    const PrioIcon = prio.icon;
                    const assignee = getAssigneeName(conv.assignedTo);
                    return (
                      <div key={conv.id} className={`flex items-start gap-2 p-3 border-b transition-colors cursor-pointer ${
                        selectedId === conv.id ? "bg-[#C2652A]/5 border-l-4 border-l-[#C2652A]" : "hover:bg-gray-100 dark:hover:bg-gray-800"
                      }`} data-testid={`conversation-item-${conv.id}`}>
                        <div className="pt-1">
                          <Checkbox checked={selectedIds.has(conv.id)} onCheckedChange={() => toggleSelectConv(conv.id)} onClick={(e) => e.stopPropagation()} />
                        </div>
                        <button className="flex-1 text-left min-w-0" onClick={() => { setSelectedId(conv.id); setSendChannel(conv.platform || "internal"); }}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#C2652A]/20 to-[#C2652A]/5 flex items-center justify-center flex-shrink-0">
                                <span className="text-sm font-semibold text-[#C2652A]">{conv.guestName.charAt(0).toUpperCase()}</span>
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <p className="font-medium text-sm truncate">{conv.guestName}</p>
                                  <PrioIcon className={`w-3 h-3 shrink-0 ${prio.color}`} />
                                </div>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <ChannelIcon className="h-3 w-3 text-gray-400" />
                                  <span className="text-xs text-gray-400 capitalize">{conv.platform}</span>
                                  {conv.source && <span className="text-[10px] text-gray-400">via {conv.source}</span>}
                                  {assignee && (
                                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                      <User className="w-2.5 h-2.5" />{assignee}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                              <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${statusColors[conv.status]}`}>{conv.status}</Badge>
                              <div className="flex items-center gap-1.5">
                                {(conv as any).unreadCount > 0 && (
                                  <span className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
                                    {(conv as any).unreadCount > 99 ? "99+" : (conv as any).unreadCount}
                                  </span>
                                )}
                                <span className="text-[10px] text-gray-400">
                                  {conv.lastMessageAt ? new Date(conv.lastMessageAt).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }) : ""}
                                </span>
                              </div>
                            </div>
                          </div>
                          {convTags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5 ml-11">
                              {convTags.slice(0, 4).map(tag => (
                                <span key={tag} className="text-[9px] px-1.5 py-0 rounded-full bg-[#C2652A]/8 text-[#C2652A] border border-[#C2652A]/15">{tag}</span>
                              ))}
                              {convTags.length > 4 && <span className="text-[9px] text-muted-foreground">+{convTags.length - 4}</span>}
                            </div>
                          )}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Conversation Detail */}
            <div className={`${selectedId ? "flex" : "hidden md:flex"} flex-col flex-1 bg-white dark:bg-gray-900`}>
              {selectedConversation ? (
                <>
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 border-b">
                    <div className="flex items-center gap-3 min-w-0">
                      <Button variant="ghost" size="sm" className="md:hidden shrink-0" onClick={() => setSelectedId(null)} data-testid="back-to-list">
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#C2652A]/20 to-[#C2652A]/5 flex items-center justify-center shrink-0">
                        <span className="text-lg font-semibold text-[#C2652A]">{selectedConversation.guestName.charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-sm truncate" data-testid="conversation-guest-name">{selectedConversation.guestName}</h3>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                          {selectedConversation.guestEmail && <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3 shrink-0" />{selectedConversation.guestEmail}</span>}
                          {selectedConversation.guestPhone && <span className="flex items-center gap-1"><Phone className="h-3 w-3 shrink-0" />{selectedConversation.guestPhone}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Select value={selectedConversation.pipelineStage || "new"} onValueChange={(v) => updateConversation.mutate({ id: selectedConversation.id, pipelineStage: v })}>
                        <SelectTrigger className="h-8 w-28 text-xs" data-testid="pipeline-stage-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{pipelineStages.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={selectedConversation.status} onValueChange={(v) => updateConversation.mutate({ id: selectedConversation.id, status: v })}>
                        <SelectTrigger className="h-8 w-24 text-xs" data-testid="status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant={showContactPanel ? "secondary" : "ghost"} size="sm" className="hidden lg:flex" onClick={() => setShowContactPanel(!showContactPanel)} data-testid="toggle-contact-panel">
                        <User className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-1 overflow-hidden">
                    {/* Main Content Area */}
                    <div className="flex flex-col flex-1">
                      {/* Detail Tabs */}
                      <div className="border-b px-3">
                        <div className="flex items-center gap-1">
                          {[
                            { id: "messages" as const, label: "Messages", icon: MessageCircle, count: messages.length },
                            { id: "activity" as const, label: "Activity", icon: History, count: activities.length },
                            { id: "tasks" as const, label: "Tasks", icon: CheckSquare, count: tasks.filter(t => t.status === 'pending').length },
                          ].map(tab => (
                            <button
                              key={tab.id}
                              onClick={() => setDetailTab(tab.id)}
                              className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                                detailTab === tab.id
                                  ? "border-[#C2652A] text-[#C2652A]"
                                  : "border-transparent text-muted-foreground hover:text-foreground"
                              }`}
                              data-testid={`tab-${tab.id}`}
                            >
                              <tab.icon className="w-3.5 h-3.5" />
                              {tab.label}
                              {tab.count > 0 && (
                                <Badge variant="secondary" className="text-[9px] px-1 py-0 min-w-[16px] h-4">{tab.count}</Badge>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Messages Tab */}
                      {detailTab === "messages" && (
                        <>
                          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="message-thread">
                            {messages.length === 0 ? (
                              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                                <MessageCircle className="h-12 w-12 mb-3 opacity-30" />
                                <p className="font-medium">No messages yet</p>
                                <p className="text-sm">Send the first message below</p>
                              </div>
                            ) : (
                              messages.map((msg) => (
                                <div key={msg.id} className={`flex ${msg.direction === "outbound" ? "justify-end" : "justify-start"}`} data-testid={`message-${msg.id}`}>
                                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                                    msg.direction === "outbound"
                                      ? "bg-[#C2652A] text-white rounded-br-md"
                                      : "bg-gray-100 dark:bg-gray-800 rounded-bl-md"
                                  }`}>
                                    {msg.senderName && (
                                      <p className={`text-[10px] font-medium mb-1 ${msg.direction === "outbound" ? "text-white/70" : "text-gray-500"}`}>{msg.senderName}</p>
                                    )}
                                    <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                                    <div className={`flex items-center gap-1.5 mt-1 ${msg.direction === "outbound" ? "text-white/60" : "text-gray-400"}`}>
                                      <Clock className="h-3 w-3" />
                                      <span className="text-[10px]">{msg.sentAt ? new Date(msg.sentAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                                      {msg.channel && <Badge variant="outline" className={`text-[9px] px-1 py-0 ${channelColors[msg.channel] || ""}`}>{msg.channel}</Badge>}
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                            <div ref={messagesEndRef} />
                          </div>

                          {/* Composer */}
                          <div className="p-3 border-t bg-gray-50/50 dark:bg-gray-950">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] text-muted-foreground mr-1">via:</span>
                                {(["internal", "email", "sms", "whatsapp"] as const).map((ch) => {
                                  const Icon = channelIcons[ch];
                                  return (
                                    <button key={ch} onClick={() => setSendChannel(ch)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors ${
                                      sendChannel === ch ? channelColors[ch] : "text-gray-400"
                                    }`} data-testid={`channel-switch-${ch}`}>
                                      <Icon className="h-3 w-3" /><span className="hidden sm:inline capitalize">{ch}</span>
                                    </button>
                                  );
                                })}
                              </div>
                              {/* Templates dropdown */}
                              {templates.length > 0 && (
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" data-testid="use-template-btn">
                                      <Sparkles className="w-3 h-3" /> Templates
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="sm:max-w-md">
                                    <DialogHeader><DialogTitle>Quick Reply Templates</DialogTitle><DialogDescription className="sr-only">Choose a template for quick reply</DialogDescription></DialogHeader>
                                    <div className="space-y-2 max-h-[50vh] overflow-y-auto pt-2">
                                      {templates.map(t => (
                                        <button key={t.id} onClick={() => { useTemplate(t); }} className="w-full text-left p-3 rounded-lg border hover:bg-muted/50 transition-colors" data-testid={`template-${t.id}`}>
                                          <div className="flex items-center justify-between mb-1">
                                            <p className="text-sm font-medium">{t.name}</p>
                                            <Badge variant="outline" className="text-[9px] px-1">{t.category}</Badge>
                                          </div>
                                          <p className="text-xs text-muted-foreground line-clamp-2">{t.body}</p>
                                        </button>
                                      ))}
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Textarea
                                placeholder={`Type your message...`}
                                value={messageText}
                                onChange={(e) => setMessageText(e.target.value)}
                                className="min-h-[44px] max-h-32 resize-none"
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    if (messageText.trim()) sendMessage.mutate({ body: messageText.trim(), direction: "outbound", channel: sendChannel, senderName: "Admin" });
                                  }
                                }}
                                data-testid="message-input"
                              />
                              <Button className="bg-[#C2652A] hover:bg-[#A8531E] self-end" size="icon" disabled={!messageText.trim() || sendMessage.isPending}
                                onClick={() => { if (messageText.trim()) sendMessage.mutate({ body: messageText.trim(), direction: "outbound", channel: sendChannel, senderName: "Admin" }); }}
                                data-testid="send-message-btn">
                                <Send className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </>
                      )}

                      {/* Activity Tab */}
                      {detailTab === "activity" && (
                        <div className="flex-1 overflow-y-auto p-4" data-testid="activity-timeline">
                          {activities.length === 0 && messages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                              <History className="h-12 w-12 mb-3 opacity-30" />
                              <p className="font-medium">No activity yet</p>
                              <p className="text-sm">Activity will appear as changes are made</p>
                            </div>
                          ) : (
                            <div className="relative">
                              <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
                              <div className="space-y-4">
                                {/* Merge activities and messages into timeline */}
                                {[
                                  ...activities.map(a => ({ ...a, _type: 'activity' as const, _time: a.createdAt })),
                                  ...messages.map(m => ({
                                    ...m,
                                    _type: 'message' as const,
                                    _time: m.sentAt,
                                    type: m.direction === 'outbound' ? 'message_sent' : 'message_received',
                                    description: m.body,
                                    performedBy: m.senderName || (m.direction === 'outbound' ? 'Admin' : selectedConversation?.guestName),
                                  })),
                                ]
                                  .sort((a, b) => new Date(b._time || 0).getTime() - new Date(a._time || 0).getTime())
                                  .slice(0, 50)
                                  .map((item, i) => {
                                    const Icon = activityTypeIcons[item.type] || CircleDot;
                                    const isMessage = item._type === 'message';
                                    return (
                                      <div key={`${item._type}-${item.id}`} className="relative flex items-start gap-3 pl-2" data-testid={`activity-item-${i}`}>
                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 z-10 ${
                                          isMessage ? (item.type === 'message_sent' ? 'bg-[#C2652A]/10 text-[#C2652A]' : 'bg-blue-100 text-blue-600 dark:bg-blue-900/50')
                                          : 'bg-muted text-muted-foreground'
                                        }`}>
                                          <Icon className="w-3 h-3" />
                                        </div>
                                        <div className="flex-1 min-w-0 pb-1">
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium">{item.performedBy || 'System'}</span>
                                            <span className="text-[10px] text-muted-foreground">
                                              {item._time ? new Date(item._time).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                            </span>
                                          </div>
                                          <p className={`text-xs mt-0.5 ${isMessage ? 'text-foreground line-clamp-2' : 'text-muted-foreground'}`}>
                                            {isMessage ? `${item.type === 'message_sent' ? 'Sent' : 'Received'}: "${(item.description || '').substring(0, 100)}${(item.description || '').length > 100 ? '...' : ''}"` : item.description}
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Tasks Tab */}
                      {detailTab === "tasks" && (
                        <div className="flex-1 overflow-y-auto p-4" data-testid="tasks-panel">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-sm font-semibold flex items-center gap-2">
                              <CheckSquare className="w-4 h-4 text-[#C2652A]" />
                              Follow-up Tasks
                            </h4>
                            <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
                              <DialogTrigger asChild>
                                <Button variant="outline" size="sm" className="h-7 text-xs gap-1" data-testid="add-task-btn">
                                  <Plus className="w-3 h-3" /> Add Task
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="sm:max-w-sm">
                                <DialogHeader><DialogTitle>New Task</DialogTitle><DialogDescription className="sr-only">Create a follow-up task</DialogDescription></DialogHeader>
                                <div className="space-y-3 pt-2">
                                  <Input placeholder="Task title..." value={newTaskTitle} onChange={e => setNewTaskTitle(e.target.value)} data-testid="input-task-title" />
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="text-xs text-muted-foreground mb-1 block">Due Date</label>
                                      <Input type="datetime-local" value={newTaskDue} onChange={e => setNewTaskDue(e.target.value)} data-testid="input-task-due" />
                                    </div>
                                    <div>
                                      <label className="text-xs text-muted-foreground mb-1 block">Priority</label>
                                      <Select value={newTaskPriority} onValueChange={setNewTaskPriority}>
                                        <SelectTrigger className="h-9" data-testid="select-task-priority"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="low">Low</SelectItem>
                                          <SelectItem value="normal">Normal</SelectItem>
                                          <SelectItem value="high">High</SelectItem>
                                          <SelectItem value="urgent">Urgent</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                  <Button className="w-full bg-[#C2652A] hover:bg-[#A8531E]" disabled={!newTaskTitle.trim() || createTask.isPending}
                                    onClick={() => createTask.mutate({ title: newTaskTitle.trim(), dueDate: newTaskDue || null, priority: newTaskPriority })}
                                    data-testid="create-task-btn">
                                    Create Task
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>

                          {tasks.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                              <CheckSquare className="h-10 w-10 mb-3 opacity-20" />
                              <p className="text-sm font-medium">No tasks yet</p>
                              <p className="text-xs mt-1">Add follow-up tasks to stay organized</p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {tasks.map(task => {
                                const isOverdue = task.status === 'pending' && task.dueDate && new Date(task.dueDate) < new Date();
                                const taskPrio = priorityConfig[task.priority || 'normal'];
                                return (
                                  <div key={task.id} className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                                    task.status === 'completed' ? 'bg-muted/30 opacity-60' : isOverdue ? 'border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-950/20' : 'hover:bg-muted/30'
                                  }`} data-testid={`task-${task.id}`}>
                                    <button onClick={() => updateTask.mutate({ id: task.id, status: task.status === 'completed' ? 'pending' : 'completed' })} className="pt-0.5" data-testid={`toggle-task-${task.id}`}>
                                      {task.status === 'completed'
                                        ? <CheckCircle2 className="w-5 h-5 text-green-500" />
                                        : <Circle className={`w-5 h-5 ${isOverdue ? 'text-red-400' : 'text-gray-300'}`} />}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>{task.title}</p>
                                      <div className="flex items-center gap-2 mt-1">
                                        {task.dueDate && (
                                          <span className={`text-[10px] flex items-center gap-1 ${isOverdue ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                                            <CalendarDays className="w-3 h-3" />
                                            {new Date(task.dueDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                          </span>
                                        )}
                                        <span className={`text-[10px] ${taskPrio.color}`}>{taskPrio.label}</span>
                                      </div>
                                    </div>
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100" onClick={() => setDeleteConfirmCrmTask(task)}>
                                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Contact Panel (Right Side) */}
                    {showContactPanel && selectedConversation && (
                      <div className="hidden lg:flex flex-col w-72 xl:w-80 border-l bg-gray-50/50 dark:bg-gray-950 overflow-y-auto" data-testid="contact-panel">
                        {/* Contact Header */}
                        <div className="p-4 border-b">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Contact</h4>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setEditingContact({
                                guestName: selectedConversation.guestName || '',
                                guestEmail: selectedConversation.guestEmail || '',
                                guestPhone: selectedConversation.guestPhone || '',
                                guestAddress: (selectedConversation as any).guestAddress || '',
                                guestCompany: (selectedConversation as any).guestCompany || '',
                                guestLanguage: (selectedConversation as any).guestLanguage || 'en',
                                source: selectedConversation.source || '',
                              })} data-testid="btn-edit-contact"><Edit3 className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowContactPanel(false)}><X className="h-3.5 w-3.5" /></Button>
                            </div>
                          </div>
                          <div className="flex flex-col items-center mb-4">
                            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#C2652A]/20 to-[#C2652A]/5 flex items-center justify-center mb-2">
                              <span className="text-2xl font-bold text-[#C2652A]">{selectedConversation.guestName.charAt(0).toUpperCase()}</span>
                            </div>
                            <p className="font-semibold text-sm">{selectedConversation.guestName}</p>
                            <p className="text-xs text-muted-foreground capitalize">{selectedConversation.platform} contact</p>
                            {enrichedData && enrichedData.bookingCount > 0 && (
                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline" className="text-[10px] px-1.5">
                                  <BookOpen className="w-3 h-3 mr-1" />{enrichedData.bookingCount} stays
                                </Badge>
                                <Badge variant="outline" className="text-[10px] px-1.5 text-green-600">
                                  <DollarSign className="w-3 h-3 mr-0.5" />${Number(enrichedData.totalRevenue || 0).toFixed(0)}
                                </Badge>
                              </div>
                            )}
                          </div>
                          <div className="space-y-2 text-xs">
                            {selectedConversation.guestEmail && (
                              <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-muted-foreground" /><span className="truncate">{selectedConversation.guestEmail}</span></div>
                            )}
                            {selectedConversation.guestPhone && (
                              <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-muted-foreground" /><span>{selectedConversation.guestPhone}</span></div>
                            )}
                            {(selectedConversation as any).guestAddress && (
                              <div className="flex items-center gap-2"><Globe className="w-3.5 h-3.5 text-muted-foreground" /><span className="truncate">{(selectedConversation as any).guestAddress}</span></div>
                            )}
                            {(selectedConversation as any).guestCompany && (
                              <div className="flex items-center gap-2"><Building className="w-3.5 h-3.5 text-muted-foreground" /><span>{(selectedConversation as any).guestCompany}</span></div>
                            )}
                            {selectedConversation.source && (
                              <div className="flex items-center gap-2"><Globe className="w-3.5 h-3.5 text-muted-foreground" /><span>Source: {selectedConversation.source}</span></div>
                            )}
                            <div className="flex items-center gap-2">
                              <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                              <span>Created {selectedConversation.createdAt ? new Date(selectedConversation.createdAt).toLocaleDateString("en-US", { timeZone: "UTC" }) : 'N/A'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Assignment */}
                        <div className="p-4 border-b">
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                            <Users className="w-3 h-3" /> Assigned To
                          </h4>
                          <Select
                            value={selectedConversation.assignedTo ? String(selectedConversation.assignedTo) : "unassigned"}
                            onValueChange={(v) => updateConversation.mutate({ id: selectedConversation.id, assignedTo: v === "unassigned" ? null : Number(v) })}
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid="assign-user"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {teamMembers.map((m: any) => (
                                <SelectItem key={m.id} value={String(m.id)}>{m.fullName || m.username}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Priority */}
                        <div className="p-4 border-b">
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Priority</h4>
                          <Select value={selectedConversation.priority || "normal"} onValueChange={(v) => updateConversation.mutate({ id: selectedConversation.id, priority: v })}>
                            <SelectTrigger className="h-8 text-xs" data-testid="contact-priority"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="urgent">Urgent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Linked Records */}
                        {(selectedConversation.reservationId || selectedConversation.apartmentId || (enrichedData?.bookingHistory?.length > 0)) && (
                          <div className="p-4 border-b">
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Booking History</h4>
                            <div className="space-y-2">
                              {selectedConversation.reservationId && (
                                <a href={`/admin/reservations`} className="flex items-center gap-2 text-xs text-[#C2652A] hover:underline" data-testid="link-reservation">
                                  <FileText className="w-3.5 h-3.5" />Reservation #{selectedConversation.reservationId}
                                </a>
                              )}
                              {selectedConversation.apartmentId && (
                                <a href={`/admin/apartments/${selectedConversation.apartmentId}`} className="flex items-center gap-2 text-xs text-[#C2652A] hover:underline" data-testid="link-apartment">
                                  <Building className="w-3.5 h-3.5" />Apartment #{selectedConversation.apartmentId}
                                </a>
                              )}
                              {enrichedData?.bookingHistory?.slice(0, 5).map((booking: any) => (
                                <div key={booking.id} className="flex items-center justify-between text-xs p-2 rounded bg-muted/50">
                                  <div>
                                    <span className="font-medium">#{booking.id}</span>
                                    <span className="text-muted-foreground ml-1">
                                      {booking.checkInDate ? new Date(booking.checkInDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : ''}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="secondary" className="text-[9px] px-1">{booking.status}</Badge>
                                    {booking.paidAmount && <span className="text-green-600 font-medium">${Number(booking.paidAmount).toFixed(0)}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Tags */}
                        <div className="p-4 border-b">
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                            <Tag className="w-3 h-3" /> Tags
                          </h4>
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {((selectedConversation.tags as string[]) || []).map(tag => (
                              <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-[#C2652A]/10 text-[#C2652A] border border-[#C2652A]/20 flex items-center gap-1">
                                {tag}
                                <button onClick={() => removeTag(tag)} className="hover:text-red-500"><X className="w-2.5 h-2.5" /></button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-1">
                            <Input placeholder="Add tag..." value={tagInput} onChange={(e) => setTagInput(e.target.value)} className="h-7 text-xs"
                              onKeyDown={(e) => { if (e.key === "Enter") addTag(tagInput); }} data-testid="tag-input" />
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => addTag(tagInput)}><Plus className="w-3 h-3" /></Button>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {defaultTags.filter(t => !((selectedConversation.tags as string[]) || []).includes(t)).slice(0, 5).map(tag => (
                              <button key={tag} onClick={() => addTag(tag)} className="text-[9px] px-1.5 py-0 rounded-full bg-muted text-muted-foreground">+ {tag}</button>
                            ))}
                          </div>
                        </div>

                        {/* Notes */}
                        <div className="p-4 border-b">
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                            <StickyNote className="w-3 h-3" /> Notes
                          </h4>
                          <Textarea placeholder="Add notes..." value={noteText} onChange={(e) => setNoteText(e.target.value)} className="text-xs min-h-[60px] resize-none" data-testid="notes-textarea" />
                          <Button variant="outline" size="sm" className="mt-2 w-full text-xs h-7" onClick={saveNotes} data-testid="save-notes-btn">Save Notes</Button>
                        </div>

                        {/* Templates Management */}
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                              <Sparkles className="w-3 h-3" /> Templates
                            </h4>
                            <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2"><Plus className="w-3 h-3 mr-0.5" />New</Button>
                              </DialogTrigger>
                              <DialogContent className="sm:max-w-md">
                                <DialogHeader><DialogTitle>{editingTemplate ? 'Edit Template' : 'New Template'}</DialogTitle><DialogDescription className="sr-only">Manage message template</DialogDescription></DialogHeader>
                                <div className="space-y-3 pt-2">
                                  <Input placeholder="Template name" value={editingTemplate ? editingTemplate.name : newTemplateName}
                                    onChange={e => editingTemplate ? setEditingTemplate({...editingTemplate, name: e.target.value}) : setNewTemplateName(e.target.value)} data-testid="input-template-name" />
                                  <Select value={editingTemplate ? editingTemplate.category : newTemplateCategory}
                                    onValueChange={v => editingTemplate ? setEditingTemplate({...editingTemplate, category: v}) : setNewTemplateCategory(v)}>
                                    <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
                                    <SelectContent>
                                      {templateCategories.map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                  <Textarea placeholder="Template body... Use {{guestName}}, {{guestEmail}}, {{guestPhone}} for variables"
                                    value={editingTemplate ? editingTemplate.body : newTemplateBody}
                                    onChange={e => editingTemplate ? setEditingTemplate({...editingTemplate, body: e.target.value}) : setNewTemplateBody(e.target.value)}
                                    className="min-h-[100px]" data-testid="input-template-body" />
                                  <div className="flex flex-wrap gap-1">
                                    {['{{guestName}}', '{{guestEmail}}', '{{guestPhone}}'].map(v => (
                                      <button key={v} type="button" className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300"
                                        onClick={() => {
                                          if (editingTemplate) setEditingTemplate({...editingTemplate, body: editingTemplate.body + v});
                                          else setNewTemplateBody(prev => prev + v);
                                        }}>
                                        {v}
                                      </button>
                                    ))}
                                  </div>
                                  <Button className="w-full bg-[#C2652A] hover:bg-[#A8531E]"
                                    disabled={editingTemplate ? !editingTemplate.name || !editingTemplate.body : !newTemplateName || !newTemplateBody}
                                    onClick={() => {
                                      if (editingTemplate) {
                                        updateTemplate.mutate({ id: editingTemplate.id, name: editingTemplate.name, body: editingTemplate.body, category: editingTemplate.category });
                                        setTemplateDialogOpen(false);
                                      } else {
                                        createTemplate.mutate({ name: newTemplateName, body: newTemplateBody, category: newTemplateCategory });
                                        setTemplateDialogOpen(false);
                                      }
                                    }}
                                    data-testid="save-template-btn">
                                    {editingTemplate ? 'Update' : 'Save'} Template
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>
                          <div className="space-y-1.5">
                            {templates.slice(0, 5).map(t => (
                              <div key={t.id} className="flex items-center justify-between text-xs p-2 rounded hover:bg-muted/50 group" data-testid={`template-item-${t.id}`}>
                                <button className="text-left flex-1 truncate" onClick={() => useTemplate(t)}>{t.name}</button>
                                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                                  <button onClick={() => { setEditingTemplate(t); setTemplateDialogOpen(true); }} className="p-1 hover:text-[#C2652A]"><Edit3 className="w-3 h-3" /></button>
                                  <button onClick={() => setDeleteConfirmCrmTemplate(t)} className="p-1 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <div className="h-20 w-20 rounded-2xl bg-[#C2652A]/5 flex items-center justify-center mb-4">
                    <MessageSquare className="h-10 w-10 text-[#C2652A]/30" />
                  </div>
                  <p className="font-semibold text-lg">Select a conversation</p>
                  <p className="text-sm mt-1 text-center max-w-xs">Choose a contact from the list or create a new one to start messaging</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Dialog open={editingContact !== null} onOpenChange={(open) => !open && setEditingContact(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
            <DialogDescription>Update contact information</DialogDescription>
          </DialogHeader>
          {editingContact && (
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Name</label>
                <Input value={editingContact.guestName} onChange={(e) => setEditingContact({ ...editingContact, guestName: e.target.value })} data-testid="edit-guest-name" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Email</label>
                <Input type="email" value={editingContact.guestEmail} onChange={(e) => setEditingContact({ ...editingContact, guestEmail: e.target.value })} data-testid="edit-guest-email" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Phone</label>
                <Input value={editingContact.guestPhone} onChange={(e) => setEditingContact({ ...editingContact, guestPhone: e.target.value })} data-testid="edit-guest-phone" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Address</label>
                <Input value={editingContact.guestAddress} onChange={(e) => setEditingContact({ ...editingContact, guestAddress: e.target.value })} data-testid="edit-guest-address" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Company</label>
                <Input value={editingContact.guestCompany} onChange={(e) => setEditingContact({ ...editingContact, guestCompany: e.target.value })} data-testid="edit-guest-company" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Language</label>
                  <Select value={editingContact.guestLanguage} onValueChange={(v) => setEditingContact({ ...editingContact, guestLanguage: v })}>
                    <SelectTrigger data-testid="edit-guest-language"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                      <SelectItem value="pt">Portuguese</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Source</label>
                  <Input value={editingContact.source} onChange={(e) => setEditingContact({ ...editingContact, source: e.target.value })} placeholder="e.g. Airbnb, Direct" data-testid="edit-guest-source" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditingContact(null)}>Cancel</Button>
                <Button className="bg-[#C2652A] hover:bg-[#A8531E]" onClick={() => {
                  if (selectedConversation) {
                    updateConversation.mutate({ id: selectedConversation.id, ...editingContact });
                    setEditingContact(null);
                    toast({ title: "Contact updated" });
                  }
                }} data-testid="btn-save-contact">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteConfirmCrmTask !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmCrmTask(null); }}
        title="Delete this task?"
        description={deleteConfirmCrmTask ? <>This will permanently delete the CRM task <strong>"{deleteConfirmCrmTask.title}"</strong>. This cannot be undone.</> : null}
        confirmLabel="Delete Task"
        pending={deleteTask.isPending}
        onConfirm={() => { if (deleteConfirmCrmTask) { deleteTask.mutate(deleteConfirmCrmTask.id); setDeleteConfirmCrmTask(null); } }}
      />

      <DeleteConfirmDialog
        open={deleteConfirmCrmTemplate !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmCrmTemplate(null); }}
        title="Delete this message template?"
        description={deleteConfirmCrmTemplate ? <>This will permanently delete the message template <strong>"{deleteConfirmCrmTemplate.name}"</strong>. This cannot be undone.</> : null}
        confirmLabel="Delete Template"
        pending={deleteTemplate.isPending}
        onConfirm={() => { if (deleteConfirmCrmTemplate) { deleteTemplate.mutate(deleteConfirmCrmTemplate.id); setDeleteConfirmCrmTemplate(null); } }}
      />
    </AdminLayout>
  );
}
